import api from '../utils/api';
import { normalizeApiResponse } from '../utils/apiResponseHandler';

const dataroomService = {
  // Sub-Admin (Admin) Dataroom APIs
  // Create a new dataroom
  createDataroom: async (formData) => {
    try {
      const response = await api.post('/datarooms', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return normalizeApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Get all datarooms for the authenticated admin with query params
  getDatarooms: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== '') {
          queryParams.append(key, params[key]);
        }
      });
      const response = await api.get(`/datarooms?${queryParams.toString()}`);
      return normalizeApiResponse(response, 'datarooms');
    } catch (error) {
      throw error;
    }
  },

  // Get my datarooms - datarooms created by or assigned to the authenticated user
  getMyDatarooms: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== '') {
          queryParams.append(key, params[key]);
        }
      });
      const response = await api.get(`/my-datarooms?${queryParams.toString()}`);
      return normalizeApiResponse(response, 'datarooms');
    } catch (error) {
      throw error;
    }
  },

  // Get my datarooms list - simplified list for dropdowns/selects
  getMyDataroomsList: async () => {
    try {
      const response = await api.get('/my-datarooms/list');
      return normalizeApiResponse(response, 'datarooms');
    } catch (error) {
      throw error;
    }
  },

  // Get single dataroom details
  getDataroomDetails: async (uuid) => {
    try {
      const response = await api.get(`/datarooms/${uuid}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get folders list for copy/move operations
  getDataroomFolders: async (uuid) => {
    try {
      const response = await api.get(`/datarooms/${uuid}/folders`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Super Admin Dataroom APIs
  // Get all datarooms in the system with query params (super admin only)
  getAllDatarooms: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== '') {
          queryParams.append(key, params[key]);
        }
      });
      const response = await api.get(`/admin/datarooms?${queryParams.toString()}`);
      return normalizeApiResponse(response, 'datarooms'); // Returns normalized response with datarooms and pagination
    } catch (error) {
      throw error;
    }
  },

  // Get pending approval datarooms with query params (super admin only)
  getPendingDatarooms: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== '') {
          queryParams.append(key, params[key]);
        }
      });
      const response = await api.get(`/admin/datarooms/pending-approval?${queryParams.toString()}`);
      return normalizeApiResponse(response, 'datarooms'); // Returns normalized response with datarooms and pagination
    } catch (error) {
      throw error;
    }
  },

  // Approve a dataroom with region_id (super admin only)
  approveDataroom: async (uuid, regionId) => {
    try {
      const response = await api.post(`/admin/datarooms/${uuid}/approve`, {
        region_id: regionId
      });
      return normalizeApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Get single dataroom details (super admin)
  getDataroomDetailsAdmin: async (uuid) => {
    try {
      const response = await api.get(`/admin/datarooms/${uuid}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Update dataroom
  updateDataroom: async (uuid, formData) => {
    try {
      // Add _method=put parameter for POST request
      formData.append('_method', 'put');

      const response = await api.post(`/datarooms/${uuid}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return normalizeApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Delete dataroom
  deleteDataroom: async (uuid) => {
    try {
      const response = await api.delete(`/datarooms/${uuid}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get AWS regions list
  getAWSRegions: async () => {
    try {
      const response = await api.get('/aws-regions');
      return normalizeApiResponse(response, 'regions');
    } catch (error) {
      throw error;
    }
  },

  // Additional Super Admin APIs (if backend supports them)
  // Reject a dataroom (super admin only)
  rejectDataroom: async (uuid, reason) => {
    try {
      const response = await api.post(`/admin/datarooms/${uuid}/reject`, {
        reason: reason
      });
      return normalizeApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Suspend/Deactivate a dataroom (super admin only)
  suspendDataroom: async (uuid, reason) => {
    try {
      // Use PUT to update status to suspended (0 for inactive)
      const response = await api.put(`/admin/datarooms/${uuid}`, { 
        status: 0,  // Use numeric value for consistency with API
        reason: reason
      });
      return normalizeApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Activate/Reactivate a dataroom (super admin only)
  // Uses the approve endpoint since there's no separate activate endpoint
  activateDataroom: async (uuid, regionId = null) => {
    try {
      // If no region provided, first get the dataroom details to get existing region
      if (!regionId) {
        try {
          const dataroomResponse = await api.get(`/admin/datarooms/${uuid}`);
          const dataroom = dataroomResponse.data?.data || dataroomResponse.data;
          regionId = dataroom.region_id || dataroom.aws_region_id || '6'; // Default to region 6 if not found
        } catch (err) {
          regionId = '6'; // Default region
        }
      }
      
      // Use the approve endpoint to activate the dataroom
      const response = await api.post(`/admin/datarooms/${uuid}/approve`, { 
        region_id: regionId
      });
      return normalizeApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Toggle watermark status for dataroom
  toggleWatermark: async (dataroomUuid, enabled) => {
    try {
      const response = await api.post(`/datarooms/${dataroomUuid}/watermark/toggle`, {
        enabled: enabled
      });
      return normalizeApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Toggle dataroom status (super admin only)
  toggleDataroomStatus: async (uuid) => {
    try {
      const response = await api.post(`/admin/datarooms/${uuid}/toggle-status`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Delete dataroom as super admin
  deleteDataroomAdmin: async (uuid) => {
    try {
      const response = await api.delete(`/admin/datarooms/${uuid}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Storage Management APIs for Admin Users (Super Admin)
  // Add storage for a specific admin user
  addDataroomStorage: async (userId, data) => {
    try {
      const response = await api.post(`/admin/users/${userId}/storage`, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get storage details for a specific admin user
  getDataroomStorage: async (userId) => {
    try {
      const response = await api.get(`/admin/users/${userId}/storage`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Remove/decrease storage for a specific admin user
  removeDataroomStorage: async (userId, data) => {
    try {
      const response = await api.delete(`/admin/users/${userId}/storage`, { data });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

export default dataroomService;